import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import { isType } from 'variant';
import { Box } from 'ink';
import InkSelectInput from 'ink-select-input';
import { InputState } from '../../input-state.js';
import { InputLabel } from '../InputLabel.js';
import { Text } from '../Text.js';
import { Indicator } from './Indicator.js';
import { Item } from './Item.js';
import { SelectOptionAvailability } from './types.js';
import type { SelectOption } from './types.js';
import { Placeholder } from './Placeholder.js';

export interface SelectInputProps<T> {
  label: string;
  info?: ReactNode;
  isActive?: boolean;
  value?: InputState<SelectOption<T>>;
  options: Array<SelectOption<T>>;
  onSubmit: (selectedOption: SelectOption<T>) => void;
  limit?: number;
  initialIndex?: number;
}

export function SelectInput<T>({
  label,
  info,
  options,
  isActive,
  value,
  onSubmit,
  limit = 10,
  initialIndex,
}: SelectInputProps<T>) {
  if (!options.length) {
    throw new Error('SelectInput: options cannot be empty');
  }

  const [inputState, setInputState] = useState<InputState<SelectOption<T>>>(
    () => value ?? InputState.NotSubmitted()
  );

  const handleHighlight = useCallback(
    ({ value }: { value: SelectOption<T> }) => {
      setInputState(InputState.NotSubmitted(value));
    },
    []
  );

  const handleSelect = useCallback(
    ({ value }: { value: SelectOption<T> }) => {
      if (!isType(value.availability, SelectOptionAvailability.Disabled)) {
        setInputState(InputState.Submitted(value));
        onSubmit(value);
      }
    },
    [onSubmit]
  );

  const items = useMemo(
    () =>
      options.map((option) => ({
        key: typeof option.value === 'string' ? option.value : option.key,
        label: option.title,
        value: option,
      })),
    [options]
  );

  useEffect(() => {
    if (isActive) {
      setInputState(InputState.NotSubmitted(inputState.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    if (value) {
      setInputState(value);
    }
  }, [value, setInputState]);

  return (
    <>
      <Box marginLeft={-2} marginBottom={1}>
        <Text>
          <InputLabel>{label}</InputLabel>{' '}
          {info && isType(inputState, InputState.NotSubmitted) && (
            <Text skin="secondary">{info}</Text>
          )}
        </Text>
      </Box>
      <Placeholder inputState={inputState} />
      {isType(inputState, InputState.NotSubmitted) && (
        <Box>
          <InkSelectInput<SelectOption<T>>
            items={items}
            limit={limit}
            // @ts-expect-error invalid types
            itemComponent={Item}
            indicatorComponent={Indicator}
            onSelect={handleSelect}
            onHighlight={handleHighlight}
            initialIndex={initialIndex}
          />
        </Box>
      )}
    </>
  );
}
